import {Separator, useFormContext} from 'platform/components';
import {VStack} from 'platform/foundation';

import {Controller} from 'react-hook-form';

import i18n from '@dms/i18n';

import {ALLOWED_SELLER_TYPE_KEYS} from '../../../constants/allowedSellerTypeKeys';
import {useApiEnums} from '../../../hooks/useApiEnums';
import {PresetEditorFormValues} from '../utils/presetEditorFormSchema';
import {SellerTypePropertyChips} from './SellerTypePropertyChips';
import {SeparatorWithLabel} from './SeparatorWithLabel';
import {ThreeWayPropertyChips} from './ThreeWayPropertyChips';
import {TwoWayPropertyChips} from './TwoWayPropertyChips';

interface PropertiesSectionProps {
  isReadOnly?: boolean;
}

export function PropertiesSection(props: PropertiesSectionProps) {
  const {isReadOnly} = props;
  const {control} = useFormContext<PresetEditorFormValues>();
  const {getEnum} = useApiEnums();

  const sellerTypeOptions = getEnum('seller_type')
    .filter((sellerType) => ALLOWED_SELLER_TYPE_KEYS.includes(sellerType.key))
    .map((sellerType) => ({
      value: sellerType.value,
      label: sellerType.label,
    }));

  return (
    <VStack spacing={2}>
      <SeparatorWithLabel label={i18n.t('entity.sourcing.relativeFilters.dialog.properties')} />

      <Controller
        name="trimLevel"
        control={control}
        render={({field}) => (
          <ThreeWayPropertyChips
            label={i18n.t('entity.sourcing.relativeFilters.fields.trimLevel')}
            value={field.value}
            onChange={field.onChange}
            sameAndBetterHint={i18n.t('entity.sourcing.relativeFilters.hint.trimSameAndBetter')}
            isReadOnly={isReadOnly}
          />
        )}
      />

      <Separator spacing={1} />

      <Controller
        name="transmission"
        control={control}
        render={({field}) => (
          <ThreeWayPropertyChips
            label={i18n.t('entity.sourcing.relativeFilters.fields.transmission')}
            value={field.value}
            onChange={field.onChange}
            sameAndBetterHint={i18n.t(
              'entity.sourcing.relativeFilters.hint.transmissionSameAndBetter'
            )}
            isReadOnly={isReadOnly}
          />
        )}
      />

      <Separator spacing={1} />

      <Controller
        name="fuel"
        control={control}
        render={({field}) => (
          <TwoWayPropertyChips
            label={i18n.t('entity.sourcing.vehiclePreview.labels.fuel')}
            value={field.value}
            onChange={field.onChange}
            isReadOnly={isReadOnly}
          />
        )}
      />

      <Separator spacing={1} />

      <Controller
        name="body"
        control={control}
        render={({field}) => (
          <TwoWayPropertyChips
            label={i18n.t('entity.sourcing.vehiclePreview.labels.bodyType')}
            value={field.value}
            onChange={field.onChange}
            isReadOnly={isReadOnly}
          />
        )}
      />

      <Separator spacing={1} />

      <Controller
        name="drive"
        control={control}
        render={({field}) => (
          <ThreeWayPropertyChips
            label={i18n.t('entity.sourcing.vehiclePreview.labels.differential')}
            value={field.value}
            onChange={field.onChange}
            sameAndBetterHint={i18n.t('entity.sourcing.relativeFilters.hint.driveSameAndBetter')}
            isReadOnly={isReadOnly}
          />
        )}
      />

      <Separator spacing={1} />

      <Controller
        name="vatDeductible"
        control={control}
        render={({field}) => (
          <ThreeWayPropertyChips
            label={i18n.t('entity.sourcing.relativeFilters.fields.vatDeductible')}
            value={field.value}
            onChange={field.onChange}
            sameAndBetterHint={i18n.t('entity.sourcing.relativeFilters.hint.vatSameAndBetter')}
            isReadOnly={isReadOnly}
          />
        )}
      />

      <Separator spacing={1} />

      <Controller
        name="sellerType"
        control={control}
        render={({field}) => (
          <SellerTypePropertyChips
            label={i18n.t('page.filters.labels.sellerType')}
            value={field.value}
            onChange={field.onChange}
            options={sellerTypeOptions}
            isReadOnly={isReadOnly}
          />
        )}
      />

      <Separator spacing={1} />

      <Controller
        name="seats"
        control={control}
        render={({field}) => (
          <ThreeWayPropertyChips
            label={i18n.t('entity.sourcing.relativeFilters.fields.seats')}
            value={field.value}
            onChange={field.onChange}
            isReadOnly={isReadOnly}
          />
        )}
      />
    </VStack>
  );
}
